import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import { Suspense, lazy } from 'react';
const Analytics = lazy(() => import('./components/Analytics'));
import Home from '@/pages/Home';
// Secondary routes are code-split so the landing page ships the smallest possible bundle.
const TalentProfile = lazy(() => import('@/pages/TalentProfile'));
const ServiceDetail = lazy(() => import('@/pages/ServiceDetail'));
const MetaAds = lazy(() => import('@/pages/MetaAds'));
const HuntrPage = lazy(() => import('@/pages/Huntr'));
const ThankYou = lazy(() => import('@/pages/ThankYou'));
const PitchBrand = lazy(() => import('@/pages/PitchBrand'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Insights = lazy(() => import('@/pages/Insights'));
const Support = lazy(() => import('@/pages/Support'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const RankrPage = lazy(() => import('@/pages/Rankr'));
const CaseStudies = lazy(() => import('@/pages/CaseStudies'));
// Add page imports here

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Suspense
      fallback={
        <div className="fixed inset-0 flex items-center justify-center bg-[#0B0B0B]">
          <div className="w-8 h-8 border-4 border-white/15 border-t-[#2563EB] rounded-full animate-spin"></div>
        </div>
      }
    >
    <Routes>
      <Route path="/" element={<Home />} />

      <Route path="/talent/:slug" element={<TalentProfile />} />
      <Route path="/services/:slug" element={<ServiceDetail />} />
      <Route path="/meta-ads" element={<MetaAds />} />
      <Route path="/huntr" element={<HuntrPage />} />
      <Route path="/rankr" element={<RankrPage />} />
      <Route path="/case-studies" element={<CaseStudies />} />
      <Route path="/pitch-a-brand" element={<PitchBrand />} />
      <Route path="/thank-you" element={<ThankYou />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/support" element={<Support />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Analytics />
          </Suspense>
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App